// Copyright (c) StrangeBee 2023

import React from 'react';
import { actionListRenderer, ActionListRendererParams } from '@Helpers/actionListRenderer';
import { DashboardInput, DashboardOutput, DescribeOutput } from 'thehive-sdk';
import { DashboardEventList } from './eventList';
import { convertOldDashboard } from '@Helpers/dashboard';
import { ICONS } from '@Constants/iconsApp';
import { DashboardManage } from './DashboardDrawer/DashboardManage';
import { downloadFile } from '@Helpers/download';

export interface DashboardActionListParams {
    locale: (label: string) => string;
    eventList: DashboardEventList;
    describeData?: DescribeOutput;
}

export const actionList = ({
    locale,
    eventList,
    describeData,
}: DashboardActionListParams): ActionListRendererParams<DashboardOutput> =>
    actionListRenderer<DashboardOutput>((selectedItems) => {
        const dashboard = selectedItems[0];

        return {
            convert: {
                label: locale('dashboard.actions.convert.label'),
                icon: <ICONS.SettingIcon />,
                query: () => {
                    const converted =
                        dashboard.version !== 1
                            ? (convertOldDashboard(dashboard, describeData) as DashboardInput)
                            : dashboard;

                    eventList.onEdit(converted, dashboard._id);
                },
                hidden: dashboard.version === 1,
            },
            edit: {
                CustomComponent: (triggerElem) => (
                    <DashboardManage
                        initialValues={dashboard}
                        isEditing={true}
                        onFinish={(values) => {
                            if (Object.keys(values).length && values['owner']) {
                                // This case is for "manageUser" right on edit mode
                                eventList.onChangeOwner(values['owner'], dashboard._id);
                            }
                            return eventList.onEdit(values, dashboard._id);
                        }}
                        triggerElem={triggerElem}
                        submitTouchedValues
                    />
                ),
                label: locale('general.edit'),
                icon: <ICONS.EditIcon />,
                scope: 'single',
            },
            duplicate: {
                CustomComponent: (triggerElem) => (
                    <DashboardManage
                        initialValues={{
                            ...dashboard,
                            title: `${locale('dashboard.duplicate.prefix')} ${dashboard.title}`,
                        }}
                        onFinish={(values) => eventList.onCreate(values)}
                        triggerElem={triggerElem}
                    />
                ),
                label: locale('general.duplicate'),
                icon: <ICONS.CopyOutlined />,
                scope: 'single',
            },
            export: {
                query: () => {
                    const { _id, title, description, group, status, definition, version } = dashboard;
                    const data = {
                        _id,
                        title,
                        description,
                        group,
                        status,
                        definition,
                        version,
                    };
                    const fileName = dashboard.title.replace(/\s/g, '_');
                    const json = JSON.stringify(data);

                    downloadFile(fileName + '.json', json, 'application/json');
                },
                label: locale('general.export'),
                icon: <ICONS.ExportFileIcon />,
                scope: 'single',
            },
            delete: {
                query: () => eventList.onDelete(dashboard?._id),
                message: locale(`dashboard.actions.delete.message`),
                label: locale('dashboard.actions.delete.label'),
                icon: <ICONS.DeleteIcon />,
                confirm: true,
            },
        };
    });
